import { Box, Center, Flex, HStack, Stack, Text, useToast, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { $count, $ok } from "foundation-ts/commons";
import { $timeBetweenDates } from "foundation-ts/date";
import { TSDay } from "foundation-ts/tsdate";
import { Nullable } from "foundation-ts/types";
import { SessionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { SignatureType } from "@bcasmart/doxecure-commons/doxecureModelTypes";

import { SessionCard } from "@components/Cards/SessionCard";
import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { AsyncConfirmModal } from "@components/Modals/AsyncConfirmModal";
import { DefaultAsyncConfirmModalState } from "@components/Modals/ModalConstants";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { SectionBox } from "@components/SectionBox/SectionBox";
import { G1Button } from "@components/G1Button";
import { SeaShellBox } from "@components/SeaShellBox";
import { useAuthContext } from "@hooks/useAuthContext";
import { useStyles } from "@hooks/useStyles";
import useTemplateSessions from "@hooks/useTemplateSessions";
import { $g1Color } from "@/theme/G1Style";
import { okOrError, optlog } from "@utils/functions";
import { BaseViewProps, NeededPersonRole } from "@utils/TypesAndConstants";
import { G1Icon } from "@utils/icons";
import { deleteTemplateSession } from "@services/session.service";
import { defaultErrorToast, defaultSuccessToast } from "@utils/toast";
import { localtrans } from "@utils/LocalTranslations";

export const TemplateSessionsView = (props: BaseViewProps) => {
    const { lockedAccount } = props;
    const { data: templates } = useTemplateSessions() ;
    const { sectionStyle, itemStyle } = useStyles();
    const [templateSessions, setTemplateSessions] = useState<SessionDto[]>([]) ;
    const [selectedTemplateSession, setSelectedTemplateSession] = useState<Nullable<SessionDto>>(null) ;
    const [confirmModal, setConfirmModal] = useState(DefaultAsyncConfirmModalState) ;
    const { connectedProfileId } = useAuthContext() ;
    const navigate = useNavigate();
    const toast = useToast() ;
    optlog(`+++ TemplatesView initialized with profile ID '${connectedProfileId}'`) ;

    const onView = (session: SessionDto) => {
        setSelectedTemplateSession(session) ;
    }

    const onDelete = () => {
        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer le modèle '${selectedTemplateSession?.title}' ?`,
            onAccept: async () => {
                if (await deleteTemplateSession(selectedTemplateSession!.apid!)) {
                    toast(defaultSuccessToast("Le modèle a été supprimé")) ;
                    const index = templates?.findIndex(session => session.apid === selectedTemplateSession?.apid) ;
                    if (index !== -1) {
                        templates?.splice(index!, 1) ;
                        setTemplateSessions(templates!) ;
                        setSelectedTemplateSession(null) ;
                    }
                } else {
                    toast(defaultErrorToast("Erreur lors de la suppression du modèle")) ;
                }
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    }

    const onChange = () => {
        navigate(`/app/newTemplate/${selectedTemplateSession?.apid}`) ;
    }

    const onCreateDraft = () => {
        navigate(`/app/newSignature/${selectedTemplateSession?.apid}?template=true`) ;
    }

    const resetAndBack = () => {
        setSelectedTemplateSession(null) ;
    }

    const renderSessionsBox = (item: SessionDto) => {
        return <SessionCard key={item.apid} item={item} itemStyle={itemStyle.template} onClick={() => onView(item)} /> ;
    } ;

    const renderFutureUsers = (role: NeededPersonRole) => {
        let title = "signataires";
        if (role === NeededPersonRole.Approver) { title = "approbateurs"; }
        else if (role === NeededPersonRole.Recipient) { title = "destinataires non signataires"; }

        return (
            <Box>
                <Text decoration="underline" fontSize="md">Liste des {title} :</Text>
                {role === NeededPersonRole.Approver && selectedTemplateSession?.approvals?.map((approval, i) => <Text key={`futureapprover${i}`} fontSize="inherit">{`${approval.approver.firstName.capitalize()} ${approval.approver.lastName.toUpperCase()}`}</Text>)}
                {role === NeededPersonRole.Signer && selectedTemplateSession?.signatures?.map((signature, i) => 
                        <Text key={`futuresigner${i}`} fontSize="inherit">{`${signature.signer.firstName.capitalize()} ${signature.signer.lastName.toUpperCase()} ${[SignatureType.QSCD, SignatureType.AnyQSCD].includes(signature.signatureType) ? `(${localtrans('QSCDSignature')})` : ''}`}</Text>)
                }
                {role === NeededPersonRole.Recipient && selectedTemplateSession?.recipients?.map((user, i) => <Text key={`futurerecipient${i}`} fontSize="inherit">{`${user.firstName.capitalize()} ${user.lastName.toUpperCase()}`}</Text>)}
            </Box>
        ) ;       
    } ;

    useEffect(() => {
        if ($ok(templates)) {
            setTemplateSessions(templates!) ;
        }
    }, [templates]) ;

    return okOrError(
        connectedProfileId,
        <>
            <PageShellWithThreeColumns
                centralContent={
                    <>
                        <ColumnTitle color={$g1Color('global.write')}>
                            <Text>Modèles</Text>
                        </ColumnTitle>
                        <VStack align="stretch" spacing="5">
                            {$count(templateSessions) === 0 ? <Center width="100%" height="100%" color="gray"><h2>Aucun modèle de dossier de signature défini</h2></Center>
                                : <SectionBox items={templateSessions} sectionStyle={sectionStyle.templates} renderItem={renderSessionsBox} />
                            }
                        </VStack>
                    </>
                }
                rightContent={
                    <>
                        <ColumnTitle color={$g1Color('global.write')} visibility={$ok(selectedTemplateSession) ? "visible" : "hidden"}>
                            <Flex alignItems="center" as="button" onClick={() => resetAndBack()}>
                                <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                                <Text>Retour</Text>
                            </Flex>
                        </ColumnTitle>
                        <SeaShellBox component="form" height="auto" visibility={$ok(selectedTemplateSession) ? "visible" : "hidden"}>
                            <HStack mt="unset" height="auto" display="flex" flexDirection={{ base: "column", sm: "row" }}>
                                <VStack align="stretch" width="100%">
                                    <Text fontWeight="bold">{selectedTemplateSession?.title}</Text>
                                    <Stack align="stretch" fontSize="md">
                                        <Text fontSize="inherit" fontStyle="italic">{selectedTemplateSession?.description}</Text>
                                        <Text fontSize="inherit">{`Mis à la signature par ${selectedTemplateSession?.expeditor.firstName?.capitalize()} ${selectedTemplateSession?.expeditor.lastName.toUpperCase()}`}</Text>
                                        <Text fontSize="inherit">{`Durée de validité du dossier : ${$timeBetweenDates(selectedTemplateSession?.validity.start.dateWithoutTime(), selectedTemplateSession?.validity.end.dateWithoutTime()) / TSDay} jours`}</Text>
                                        {($count(selectedTemplateSession?.approvals) !== 0) && renderFutureUsers(NeededPersonRole.Approver)}
                                        {($count(selectedTemplateSession?.signatures) !== 0) && renderFutureUsers(NeededPersonRole.Signer)}
                                        {($count(selectedTemplateSession?.recipients) !== 0) && renderFutureUsers(NeededPersonRole.Recipient)}
                                    </Stack>
                                </VStack>

                                <Stack margin="5%" display="flex" justifyContent="center" width={{ base: "100%", sm: "unset" }} height="100%" flexDirection={{ base: "row", sm: "column" }} spacing={{ base: "unset", sm: "2" }} alignItems="center" paddingTop={{ base: "2%", sm: "unset" }}>
                                    <G1Button component="form" variant="normal" title="Modifier" width="150px" fontSize="md" disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={onChange} />
                                    <G1Button component="form" variant="delete" title={localtrans('delete')} width="150px" fontSize="md" disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={onDelete} />
                                    <G1Button component="form" variant="alternate" title="Créer un brouillon" width="150px" fontSize="md" disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={onCreateDraft} />
                                </Stack>
                            </HStack>
                        </SeaShellBox>
                    </>
                }
            />
            <AsyncConfirmModal message={confirmModal.message} onAccept={confirmModal.onAccept} onClose={confirmModal.onClose} />
        </>,
        false,
        "Impossible d'afficher la liste des modèles car l'identifiant de profil de la personne connectée n'a pas pu être récupéré.",
        "Connexion invalide"
    ) ;
};
